/**
 * Normalizes configuration data based on the specified app type (llu or libre1)
 *
 * @param rawConfig - The raw configuration object to be normalized
 * @param country - The country code for which the configuration is being normalized
 * @param app - App type ('llu' or 'libre1') that determines the normalization strategy
 * @returns A normalized Config object with standardized structure
 * @throws Will throw an error with status 400 if normalization fails or app is not supported
 */

export const normalizeConfig = (rawConfig, country, app) => {
  try {
    switch (app) {
      case 'llu':
        return normalizeLluConfig(rawConfig);
      case 'libre1':
        return normalizeLibreConfig(rawConfig, country);
      default:
        throw {
          status: 400,
          message: `App not supported: ${app}`
        };
    }
  } catch (error) {
    throw {
      status: 400,
      message: `Error normalizing config: ${error || 'Unknown error'}`
    };
  }
};
const normalizeLluConfig = rawConfig => {
  var _lluConfig$validation, _lluConfig$validation2;
  const lluConfig = rawConfig.base;
  const config = {
    minAge: lluConfig.minCaregiverAge,
    apiCountryUrls: lluConfig.lluAPICountryURLs,
    i18n: lluConfig.i18n ?? {
      allowList: [],
      fallBacks: {
        default: ['en-US']
      }
    },
    supportedLanguages: lluConfig.supportedLanguages,
    supportedCountries: Object.entries(lluConfig.supportedCountries).map(([key]) => key),
    countries: [],
    passwordRules: lluConfig.passwordRules,
    documentUrls: {
      files: lluConfig.documentsUrl
    },
    documentPaths: {
      files: {
        termsOfUse: lluConfig.documents.toullu,
        privacyNotice: lluConfig.documents.pnllu
      }
    },
    validation: {
      patient: {
        firstNameMaxLength: ((_lluConfig$validation = lluConfig.validationRules) === null || _lluConfig$validation === void 0 || (_lluConfig$validation = _lluConfig$validation.firstName) === null || _lluConfig$validation === void 0 ? void 0 : _lluConfig$validation.stringMaxLength) ?? 40,
        lastNameMaxLength: ((_lluConfig$validation2 = lluConfig.validationRules) === null || _lluConfig$validation2 === void 0 || (_lluConfig$validation2 = _lluConfig$validation2.lastName) === null || _lluConfig$validation2 === void 0 ? void 0 : _lluConfig$validation2.stringMaxLength) ?? 80
      }
    },
    androidAppURL: lluConfig.androidAppURL,
    iosAppURL: lluConfig.iosAppURL
  };
  return config;
};
const normalizeLibreConfig = (rawConfig, country) => {
  var _rawConfig$validation, _rawConfig$validation2;
  const passwordRules = rawConfig.validation.identity.map(({
    data,
    type,
    message
  }) => ({
    data,
    type,
    message
  }));
  const config = {
    minAge: rawConfig.adultAges[country],
    apiCountryUrls: rawConfig.libreApiCountryUrls,
    i18n: rawConfig.i18n ?? {
      allowList: [],
      fallBacks: {
        default: ['en-US']
      }
    },
    supportedLanguages: rawConfig.languages,
    supportedCountries: rawConfig.supportedCountries,
    countries: rawConfig.countries,
    passwordRules,
    documentUrls: rawConfig.documentUrls,
    documentPaths: {
      cms: {
        ...rawConfig.documentPaths.cms
      },
      files: {
        termsOfUse: rawConfig.documentPaths.files.toulibre,
        privacyNotice: rawConfig.documentPaths.files.pnlibre
      }
    },
    validation: {
      patient: {
        firstNameMaxLength: ((_rawConfig$validation = rawConfig.validation) === null || _rawConfig$validation === void 0 || (_rawConfig$validation = _rawConfig$validation.libreApi) === null || _rawConfig$validation === void 0 || (_rawConfig$validation = _rawConfig$validation.patient) === null || _rawConfig$validation === void 0 || (_rawConfig$validation = _rawConfig$validation.firstName) === null || _rawConfig$validation === void 0 ? void 0 : _rawConfig$validation.stringMaxLength) ?? 40,
        lastNameMaxLength: ((_rawConfig$validation2 = rawConfig.validation) === null || _rawConfig$validation2 === void 0 || (_rawConfig$validation2 = _rawConfig$validation2.libreApi) === null || _rawConfig$validation2 === void 0 || (_rawConfig$validation2 = _rawConfig$validation2.patient) === null || _rawConfig$validation2 === void 0 || (_rawConfig$validation2 = _rawConfig$validation2.lastName) === null || _rawConfig$validation2 === void 0 ? void 0 : _rawConfig$validation2.stringMaxLength) ?? 80
      }
    }
  };
  return config;
};